import { newEntityId } from '../model/document';
import type { BlockDef, CadEntity } from '../model/entity';
import { pt } from '../model/geom';
import { mm, type Micron } from '../model/units';

/**
 * 파라메트릭 블록 (작업지시서 P4-04)
 *
 * ★ 왜 필요한가 — `고도화_제안_v2.md` 5.3절
 *   곤돌라 길이는 600/900/1200 모듈 조합이고, 선반 단수는 4/5/6 단이다.
 *   조합마다 개별 블록을 만들면 라이브러리가 수백 개로 폭발하고
 *   단가가 바뀔 때마다 전부 고쳐야 한다.
 *   → 규격을 **파라미터**로 두고 도형은 생성기가 만든다.
 *
 * 설계:
 *   · 생성기는 마트 집기 계열(곤돌라·엔드매대·쇼케이스·계산대·랙)만 다룬다.
 *     그 외 블록은 `entities` 에 도형을 직접 담는 정적 블록이다.
 *   · 생성기는 **순수 함수**다. 같은 파라미터면 항상 같은 도형이 나온다.
 *   · 좌표 원점은 블록 삽입점 = 좌하단이다. 매대를 벽에 붙일 때
 *     좌하단이 기준이어야 스냅이 직관적이다.
 */

export type BlockGenerator =
  | 'box'
  | 'gondola'
  | 'endcap'
  | 'showcase'
  | 'counter'
  | 'rack'
  /** 편면 진열대 — 벽면장·행거·잡지대. 등판선이 없어 도면에서 양면과 구분된다 */
  | 'wallshelf'
  /** 평대·아일랜드 매대 — 낮은 사각 매대 */
  | 'table'
  /** 원형 덤프빈·원형 기둥 */
  | 'bin'
  /** 파렛트 */
  | 'pallet'
  /** 평대형 냉장·냉동고 — 개구부가 있는 상부 개방형 */
  | 'island'
  /** 기둥 — 대각선으로 구조물임을 표시 */
  | 'column'
  /** 셔터 */
  | 'shutter'
  /** 계단 */
  | 'stair';

/** 사용자에게 보여줄 파라미터 정의 */
export interface ParamSpec {
  key: string;
  label: string;
  unit?: string;
  /** 선택형 — 이 값들 중에서만 고를 수 있다 (모듈 규격) */
  options?: number[];
  min?: number;
  max?: number;
  step?: number;
  default: number;
}

export interface ParametricSpec {
  generator: BlockGenerator;
  params: ParamSpec[];
}

/** 블록 정의에 파라메트릭 정보가 붙은 형태 */
export interface ParametricBlockDef extends BlockDef {
  parametric?: ParametricSpec;
}

export type BlockParams = Record<string, number>;

/* -------------------------------------------------------------------------- *
 * 파라미터 정규화
 * -------------------------------------------------------------------------- */

/**
 * 사용자가 넘긴 파라미터를 스펙에 맞게 다듬는다.
 * 잘못된 값이 들어와도 도형 생성이 깨지지 않도록 여기서 전부 막는다.
 */
export function normalizeParams(spec: ParametricSpec, input: BlockParams = {}): BlockParams {
  const out: BlockParams = {};
  for (const p of spec.params) {
    let v = input[p.key];
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      v = p.default;
    }
    if (p.options && p.options.length > 0) {
      // 선택형은 가장 가까운 허용값으로 맞춘다 — 임의 치수를 막는다
      v = p.options.reduce((best, o) => (Math.abs(o - v!) < Math.abs(best - v!) ? o : best), p.options[0]!);
    } else {
      if (p.min !== undefined) v = Math.max(p.min, v);
      if (p.max !== undefined) v = Math.min(p.max, v);
      if (p.step) v = Math.round(v / p.step) * p.step;
    }
    out[p.key] = v;
  }
  return out;
}

/* -------------------------------------------------------------------------- *
 * 도형 생성
 * -------------------------------------------------------------------------- */

interface GenContext {
  layer: string;
  id: () => string;
}

/**
 * 파라미터로 블록 도형을 만든다.
 * 좌표는 블록 원점(좌하단) 기준 µm.
 */
export function generateBlockGeometry(
  spec: ParametricSpec,
  rawParams: BlockParams,
  layer: string
): CadEntity[] {
  const p = normalizeParams(spec, rawParams);
  const ctx: GenContext = { layer, id: newEntityId };

  switch (spec.generator) {
    case 'box':
      return [rect(ctx, 0, 0, mm(p.width ?? 900), mm(p.depth ?? 450))];

    case 'gondola':
      return gondola(ctx, p);

    case 'endcap':
      return endcap(ctx, p);

    case 'showcase':
      return showcase(ctx, p);

    case 'counter':
      return counter(ctx, p);

    case 'rack':
      return rack(ctx, p);

    case 'wallshelf':
      return wallshelf(ctx, p);

    case 'table':
      return table(ctx, p);

    case 'bin':
      return bin(ctx, p);

    case 'pallet':
      return pallet(ctx, p);

    case 'island':
      return island(ctx, p);

    case 'column':
      return column(ctx, p);

    case 'shutter':
      return shutter(ctx, p);

    case 'stair':
      return stair(ctx, p);
  }
}

function rect(ctx: GenContext, x: Micron, y: Micron, w: Micron, h: Micron): CadEntity {
  return {
    id: ctx.id(),
    type: 'polyline',
    layer: ctx.layer,
    closed: true,
    points: [pt(x, y), pt(x + w, y), pt(x + w, y + h), pt(x, y + h)],
  };
}

function line(ctx: GenContext, x1: Micron, y1: Micron, x2: Micron, y2: Micron): CadEntity {
  return { id: ctx.id(), type: 'line', layer: ctx.layer, a: pt(x1, y1), b: pt(x2, y2) };
}

/**
 * 곤돌라 (양면 진열대).
 * 외곽 + 가운데 등판선 + 선반 구획 표시.
 * 등판선이 있어야 도면에서 양면인지 편면인지 구분된다.
 */
function gondola(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 900);
  const d = mm(p.depth ?? 900);
  const bays = Math.max(1, Math.round(p.bays ?? 1));

  const out: CadEntity[] = [];
  const total = w * bays;

  out.push(rect(ctx, 0, 0, total, d));
  // 등판 (양면 곤돌라의 가운데 칸막이)
  out.push(line(ctx, 0, Math.round(d / 2), total, Math.round(d / 2)));
  // 베이 구획선
  for (let i = 1; i < bays; i++) {
    out.push(line(ctx, w * i, 0, w * i, d));
  }
  return out;
}

/**
 * 엔드매대 — 통로 끝. 곡면 코너를 사각으로 단순화한다.
 *
 * 폭은 `width × bays`. 진열 연장·점유 면적 계산도 같은 규칙을 쓴다 —
 * 어긋나면 도면은 1개인데 물량은 2개로 잡히는 사고가 난다.
 */
function endcap(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 1200) * Math.max(1, Math.round(p.bays ?? 1));
  const d = mm(p.depth ?? 600);
  return [
    rect(ctx, 0, 0, w, d),
    // 전면 표시 — 어느 쪽이 고객 방향인지 도면에서 보여야 한다
    line(ctx, 0, 0, w, 0),
    line(ctx, Math.round(w * 0.15), Math.round(d * 0.2), Math.round(w * 0.85), Math.round(d * 0.2)),
  ];
}

/**
 * 냉장·냉동 쇼케이스.
 * 외곽 + 도어 분할선. 도어 수는 폭에서 계산한다 (표준 도어 폭 약 750mm).
 */
function showcase(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 2400);
  const d = mm(p.depth ?? 900);
  const doorW = mm(750);

  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  const doors = Math.max(1, Math.round(w / doorW));
  const step = Math.round(w / doors);
  for (let i = 1; i < doors; i++) {
    out.push(line(ctx, step * i, 0, step * i, d));
  }
  // 후면 기계실 표시 — 설비 배치 검토에서 필요하다
  out.push(line(ctx, 0, Math.round(d * 0.75), w, Math.round(d * 0.75)));
  return out;
}

/** 계산대 — 본체 + 컨베이어 + 작업자 위치 */
function counter(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 1800);
  const d = mm(p.depth ?? 900);
  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  // POS 위치
  const px = Math.round(w * 0.7);
  out.push(rect(ctx, px, Math.round(d * 0.3), mm(400), mm(300)));
  // 작업자 통로측 표시
  out.push(line(ctx, 0, Math.round(d * 0.5), px, Math.round(d * 0.5)));
  return out;
}

/** 창고 랙 — 베이 분할 */
function rack(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 2700);
  const d = mm(p.depth ?? 1100);
  const bays = Math.max(1, Math.round(p.bays ?? 3));
  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  const step = Math.round(w / bays);
  for (let i = 1; i < bays; i++) out.push(line(ctx, step * i, 0, step * i, d));
  return out;
}

/**
 * 편면 진열대 (벽면장·행거·잡지대).
 *
 * 곤돌라와 달리 **등판선을 그리지 않는다.** 도면에서 이 선 하나가
 * 양면(통로 양쪽 진열)과 편면(벽 부착)을 가르고, 통로 폭 검증이
 * 앞면 이격만 볼지 양쪽을 볼지도 여기서 갈린다.
 */
function wallshelf(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 900);
  const d = mm(p.depth ?? 450);
  const bays = Math.max(1, Math.round(p.bays ?? 1));
  const total = w * bays;

  const out: CadEntity[] = [rect(ctx, 0, 0, total, d)];
  // 진열면 표시 — 고객이 서는 쪽(전면, y=0)
  out.push(line(ctx, 0, Math.round(d * 0.2), total, Math.round(d * 0.2)));
  for (let i = 1; i < bays; i++) out.push(line(ctx, w * i, 0, w * i, d));
  return out;
}

/** 평대·아일랜드 매대 — 외곽과 상단 단차를 이중 사각으로 표현한다 */
function table(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 1200);
  const d = mm(p.depth ?? 900);
  const inset = Math.round(Math.min(w, d) * 0.12);
  return [rect(ctx, 0, 0, w, d), rect(ctx, inset, inset, w - inset * 2, d - inset * 2)];
}

/** 원형 덤프빈·원형 기둥. 삽입점은 다른 블록과 같게 외접 사각의 좌하단이다. */
function bin(ctx: GenContext, p: BlockParams): CadEntity[] {
  const dia = mm(p.diameter ?? 900);
  const r = Math.round(dia / 2);
  return [
    { id: ctx.id(), type: 'circle', layer: ctx.layer, center: pt(r, r), radius: r },
    line(ctx, r - Math.round(r * 0.4), r, r + Math.round(r * 0.4), r),
    line(ctx, r, r - Math.round(r * 0.4), r, r + Math.round(r * 0.4)),
  ];
}

/** 파렛트 — 지게차 포크 삽입구를 표시한다 (적치 방향 검토용) */
function pallet(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 1100);
  const d = mm(p.depth ?? 1100);
  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  for (const f of [0.3, 0.7]) {
    const y = Math.round(d * f);
    out.push(line(ctx, 0, y, w, y));
  }
  return out;
}

/** 평대형 냉장·냉동고 — 외곽 + 개구부 + 도어 분할 */
function island(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 2400);
  const d = mm(p.depth ?? 1100);
  const rim = mm(150); // 개구부 테두리

  const out: CadEntity[] = [rect(ctx, 0, 0, w, d), rect(ctx, rim, rim, w - rim * 2, d - rim * 2)];
  const doors = Math.max(1, Math.round(p.doors ?? 2));
  const step = Math.round(w / doors);
  for (let i = 1; i < doors; i++) out.push(line(ctx, step * i, rim, step * i, d - rim));
  return out;
}

/** 기둥 — 대각선으로 구조물임을 표시한다 (매대와 헷갈리면 안 된다) */
function column(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 600);
  const d = mm(p.depth ?? 600);
  return [rect(ctx, 0, 0, w, d), line(ctx, 0, 0, w, d), line(ctx, 0, d, w, 0)];
}

/** 셔터 — 얇은 판 + 골 표시 */
function shutter(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 3000);
  const d = mm(p.depth ?? 200);
  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  const step = mm(300);
  for (let x = step; x < w; x += step) out.push(line(ctx, x, 0, x, d));
  return out;
}

/** 계단 — 디딤판 수만큼 선을 긋는다 */
function stair(ctx: GenContext, p: BlockParams): CadEntity[] {
  const w = mm(p.width ?? 1200);
  const d = mm(p.depth ?? 3000);
  const steps = Math.max(2, Math.round(p.steps ?? 10));
  const out: CadEntity[] = [rect(ctx, 0, 0, w, d)];
  const step = Math.round(d / steps);
  for (let i = 1; i < steps; i++) out.push(line(ctx, 0, step * i, w, step * i));
  // 진행 방향
  out.push(line(ctx, Math.round(w / 2), Math.round(step * 0.5), Math.round(w / 2), d - Math.round(step * 0.5)));
  return out;
}

/* -------------------------------------------------------------------------- *
 * 진열 지표 (P5-06 이 쓴다)
 * -------------------------------------------------------------------------- */

/**
 * 이 블록 1개가 제공하는 진열 선반 총 연장(m).
 *
 * 마트 MD·발주 부서가 바로 쓰는 숫자다.
 * "곤돌라 6열 늘리면 진열 연장이 몇 m 늘어나나?" 에 답하기 위한 것.
 */
/**
 * 생성기가 `width` 를 **베이당 폭**으로 쓰는가, **전체 폭**으로 쓰는가.
 *
 * ★ 이 표가 도형 생성기와 어긋나면 도면은 1개인데 물량은 2개로 잡힌다.
 *   새 생성기를 추가하면 여기도 반드시 같이 손봐야 한다.
 */
const WIDTH_IS_PER_BAY = new Set<BlockGenerator>(['gondola', 'endcap', 'wallshelf']);

function totalWidthM(spec: ParametricSpec, p: BlockParams): number {
  const bays = WIDTH_IS_PER_BAY.has(spec.generator) ? Math.max(1, Math.round(p.bays ?? 1)) : 1;
  return ((p.width ?? 0) / 1000) * bays;
}

export function shelfLengthMeters(spec: ParametricSpec, rawParams: BlockParams): number {
  const p = normalizeParams(spec, rawParams);
  const widthM = totalWidthM(spec, p);
  const bays = Math.max(1, Math.round(p.bays ?? 1));
  const levels = Math.max(0, Math.round(p.shelfLevels ?? 0));

  switch (spec.generator) {
    case 'gondola':
      // 양면 × 단수
      return widthM * levels * 2;
    case 'endcap':
    case 'wallshelf':
      return widthM * levels;
    case 'rack':
      // rack 의 width 는 전체 폭이므로 베이로 나뉘어 있을 뿐 연장은 그대로다
      return widthM * bays * levels;
    case 'showcase':
      return widthM * levels;
    case 'table':
    case 'island':
      // 상면 진열 — 단수 개념이 없으므로 1단으로 본다
      return widthM;
    default:
      return 0;
  }
}

/** 블록이 차지하는 바닥 면적 (㎡) */
export function footprintSquareMeters(spec: ParametricSpec, rawParams: BlockParams): number {
  const p = normalizeParams(spec, rawParams);
  if (spec.generator === 'bin') {
    const r = (p.diameter ?? 0) / 2000;
    return Math.PI * r * r;
  }
  return (totalWidthM(spec, p) * (p.depth ?? 0)) / 1000;
}

/* -------------------------------------------------------------------------- *
 * 표준 파라미터 스펙
 * -------------------------------------------------------------------------- */

const WIDTH_MODULES = [600, 900, 1200];

export const STANDARD_SPECS: Record<string, ParametricSpec> = {
  gondola: {
    generator: 'gondola',
    params: [
      { key: 'width', label: '베이 폭', unit: 'mm', options: WIDTH_MODULES, default: 900 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [600, 900, 1200], default: 900 },
      { key: 'bays', label: '베이 수', min: 1, max: 20, step: 1, default: 1 },
      { key: 'shelfLevels', label: '선반 단수', options: [4, 5, 6], default: 5 },
    ],
  },
  endcap: {
    generator: 'endcap',
    params: [
      { key: 'width', label: '폭', unit: 'mm', options: [900, 1200, 1500], default: 1200 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [600, 900], default: 600 },
      { key: 'bays', label: '베이 수', min: 1, max: 4, step: 1, default: 1 },
      { key: 'shelfLevels', label: '선반 단수', options: [3, 4, 5], default: 4 },
    ],
  },
  showcase: {
    generator: 'showcase',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 900, max: 12000, step: 300, default: 2400 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [750, 900, 1100], default: 900 },
      { key: 'shelfLevels', label: '선반 단수', options: [3, 4, 5], default: 4 },
    ],
  },
  counter: {
    generator: 'counter',
    params: [
      { key: 'width', label: '폭', unit: 'mm', options: [1500, 1800, 2100], default: 1800 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [900, 1200], default: 900 },
    ],
  },
  rack: {
    generator: 'rack',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 900, max: 5400, step: 300, default: 2700 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [800, 1100], default: 1100 },
      { key: 'bays', label: '베이 수', min: 1, max: 10, step: 1, default: 3 },
      { key: 'shelfLevels', label: '단수', min: 2, max: 8, step: 1, default: 4 },
    ],
  },
  box: {
    generator: 'box',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 100, max: 20000, step: 50, default: 900 },
      { key: 'depth', label: '깊이', unit: 'mm', min: 100, max: 5000, step: 50, default: 450 },
    ],
  },
  wallshelf: {
    generator: 'wallshelf',
    params: [
      { key: 'width', label: '베이 폭', unit: 'mm', options: WIDTH_MODULES, default: 900 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [350, 450, 600], default: 450 },
      { key: 'bays', label: '베이 수', min: 1, max: 20, step: 1, default: 1 },
      { key: 'shelfLevels', label: '선반 단수', options: [4, 5, 6, 7], default: 5 },
    ],
  },
  table: {
    generator: 'table',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 600, max: 6000, step: 100, default: 1200 },
      { key: 'depth', label: '깊이', unit: 'mm', min: 600, max: 2400, step: 100, default: 900 },
    ],
  },
  bin: {
    generator: 'bin',
    params: [{ key: 'diameter', label: '지름', unit: 'mm', min: 300, max: 1800, step: 50, default: 900 }],
  },
  pallet: {
    generator: 'pallet',
    params: [
      { key: 'width', label: '폭', unit: 'mm', options: [1100, 1200], default: 1100 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [1100, 1000, 800], default: 1100 },
    ],
  },
  island: {
    generator: 'island',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 900, max: 6000, step: 100, default: 2400 },
      { key: 'depth', label: '깊이', unit: 'mm', options: [900, 1100, 1300], default: 1100 },
      { key: 'doors', label: '도어 수', min: 1, max: 8, step: 1, default: 2 },
    ],
  },
  column: {
    generator: 'column',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 200, max: 2000, step: 50, default: 600 },
      { key: 'depth', label: '깊이', unit: 'mm', min: 200, max: 2000, step: 50, default: 600 },
    ],
  },
  shutter: {
    generator: 'shutter',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 900, max: 12000, step: 100, default: 3000 },
      { key: 'depth', label: '두께', unit: 'mm', min: 100, max: 400, step: 50, default: 200 },
    ],
  },
  stair: {
    generator: 'stair',
    params: [
      { key: 'width', label: '폭', unit: 'mm', min: 900, max: 3000, step: 100, default: 1200 },
      { key: 'depth', label: '길이', unit: 'mm', min: 1500, max: 8000, step: 100, default: 3000 },
      { key: 'steps', label: '단 수', min: 3, max: 24, step: 1, default: 10 },
    ],
  },
};
